import { useCallback, useRef, useEffect, useMemo, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/cn";
import { useUIStore, type SidebarTab } from "../../stores/ui";
import { useCollaborationStore } from "../../stores/collaboration";

interface SidebarProps {
  filesContent?: ReactNode;
  outlineContent?: ReactNode;
  commentsContent?: ReactNode;
  chatContent?: ReactNode;
  citationsContent?: ReactNode;
  contextContent?: ReactNode;
  className?: string;
}

const ALL_TABS: { id: SidebarTab; label: string }[] = [
  { id: "files", label: "Files" },
  { id: "outline", label: "Outline" },
  { id: "chat", label: "Chat" },
  { id: "citations", label: "Cite" },
  { id: "comments", label: "Comments" },
  { id: "context", label: "Context" },
];

const MIN_WIDTH = 180;
const MAX_WIDTH = 400;

const spring = { type: "spring" as const, stiffness: 400, damping: 32, mass: 0.8 };

export function Sidebar({
  filesContent,
  outlineContent,
  commentsContent,
  chatContent,
  citationsContent,
  contextContent,
  className,
}: SidebarProps) {
  const open = useUIStore((s) => s.sidebarOpen);
  const width = useUIStore((s) => s.sidebarWidth);
  const setWidth = useUIStore((s) => s.setSidebarWidth);
  const activeTab = useUIStore((s) => s.sidebarTab);
  const setActiveTab = useUIStore((s) => s.setSidebarTab);
  const dragging = useRef(false);

  const openCommentCount = useCollaborationStore(
    (s) => s.comments.filter((c) => !c.parent_id && !c.resolved).length,
  );

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragging.current = true;
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onPointerMove = (ev: PointerEvent) => {
        if (!dragging.current) return;
        setWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, ev.clientX)));
      };

      const onPointerUp = () => {
        dragging.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("pointerup", onPointerUp);
      };

      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    },
    [setWidth],
  );

  const tabContent: Record<SidebarTab, ReactNode> = {
    files: filesContent,
    outline: outlineContent,
    comments: commentsContent,
    chat: chatContent,
    citations: citationsContent,
    context: contextContent,
  };

  const visibleTabIds = useMemo(
    () => ALL_TABS.filter((tab) => tabContent[tab.id] != null).map((t) => t.id),
    [filesContent, outlineContent, commentsContent, chatContent, citationsContent, contextContent],
  );
  const visibleTabs = ALL_TABS.filter((tab) => visibleTabIds.includes(tab.id));

  useEffect(() => {
    if (visibleTabIds.length > 0 && !visibleTabIds.includes(activeTab)) {
      setActiveTab(visibleTabIds[0]);
    }
  }, [visibleTabIds, activeTab, setActiveTab]);

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.aside
          initial={{ width: 0 }}
          animate={{ width }}
          exit={{ width: 0 }}
          transition={spring}
          className={cn(
            "relative flex h-full shrink-0 flex-col overflow-hidden",
            "bg-[var(--bg-surface)] border-r border-[var(--border-hairline)]",
            className,
          )}
          style={{ willChange: "width" }}
        >
          {/* Tab bar */}
          <div className="flex shrink-0 overflow-x-auto overflow-y-hidden scrollbar-none border-b border-[var(--border-hairline)] px-1.5 pt-1.5">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "relative shrink-0 px-2.5 pb-1.5 text-[11px] font-medium tracking-wide",
                  "transition-colors duration-100",
                  activeTab === tab.id
                    ? "text-[var(--text-primary)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
                )}
              >
                <span className="flex items-center gap-1">
                  {tab.label}
                  {tab.id === "comments" && openCommentCount > 0 && (
                    <span
                      className={cn(
                        "inline-flex items-center justify-center",
                        "min-w-[14px] h-[14px] px-1 rounded-full",
                        "text-[8px] font-bold tabular-nums leading-none",
                        "bg-[var(--color-cool-azure)] text-[var(--text-inverse)]",
                      )}
                    >
                      {openCommentCount}
                    </span>
                  )}
                </span>
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="sidebar-tab-indicator"
                    className="absolute inset-x-0 bottom-0 h-[1.5px] rounded-full bg-[var(--text-primary)]"
                    transition={spring}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Content area */}
          <div
            className={cn(
              "flex-1 min-h-0 overflow-x-hidden",
              activeTab === "chat" ? "p-0 overflow-y-auto overflow-x-hidden" : "overflow-y-auto",
            )}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 4 }}
                transition={{ duration: 0.12 }}
                className={activeTab === "chat" ? "h-full" : undefined}
              >
                {tabContent[activeTab] ?? (
                  <p className="p-3 text-xs text-[var(--text-muted)]">No content</p>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Resize handle (right edge) */}
          <div
            onPointerDown={onResizePointerDown}
            className={cn(
              "group absolute right-0 top-0 z-20 h-full w-[5px] cursor-col-resize",
              "touch-none select-none",
            )}
          >
            <div
              className={cn(
                "absolute inset-y-0 right-0 w-px bg-transparent",
                "transition-all duration-150",
                "group-hover:w-[3px] group-hover:bg-[var(--text-muted)] group-hover:opacity-40",
              )}
            />
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
